import type {
  Berth,
  BerthBodyDto,
  BerthDocument,
  BerthDto,
  BerthFileDto,
  BerthFileSlot,
  BerthValues,
} from './vessel-marinas.dto';

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * The picker's local midnight → that calendar date at UTC midnight. The backend
 * stores a date, not a moment, and reads it in the string's own offset, so
 * `toISOString()` east of UTC would save the day before.
 */
const toWireDate = (iso?: string): null | string => {
  if (!iso) return null;
  const date = new Date(iso);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00Z`;
};

/** And back: the stored date at local midnight, the day it was picked in any timezone. */
const fromWireDate = (value: null | string): string | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()).toISOString();
};

const toDocuments = (slot: BerthFileSlot, file: BerthFileDto | null): BerthDocument[] =>
  file ? [{ slot, id: file.id, name: file.name, link: file.link, size: file.size }] : [];

export const mapBerth = ({
  id,
  title,
  type,
  price,
  price_period,
  date_from,
  date_to,
  location,
  contact_file,
  map_file,
  service_provider_id,
  event_id,
}: BerthDto): Berth => ({
  id,
  title,
  type,
  dateFrom: fromWireDate(date_from),
  dateTo: fromWireDate(date_to),
  price: price ? { amount: price.number, currency: price.currency } : undefined,
  pricePeriod: price_period ?? undefined,
  location,
  documents: [...toDocuments('contact-file', contact_file), ...toDocuments('map-file', map_file)],
  serviceProviderId: service_provider_id ?? undefined,
  eventId: event_id ?? undefined,
});

/** Every field, every time: PUT replaces the record, so an omitted one is cleared. */
export const mapBerthBody = ({
  title,
  type,
  price,
  pricePeriod,
  dateFrom,
  dateTo,
  location,
  serviceProviderId,
  eventId,
}: BerthValues): BerthBodyDto => ({
  title,
  type,
  price: price ? { number: price.amount, currency: price.currency } : null,
  price_period: pricePeriod ?? null,
  date_from: toWireDate(dateFrom),
  date_to: toWireDate(dateTo),
  location,
  service_provider_id: serviceProviderId ?? null,
  event_id: eventId ?? null,
});

export const berthValues = ({ id: _id, documents: _documents, ...values }: Berth): BerthValues =>
  values;
